import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Logo } from './Logo';
import { NotificationCenter } from './NotificationCenter';
import type { InfoTopicId } from './InfoMenuModal';
import { UserProfile, AppNotification } from '../types';
import { 
  Shield, 
  User, 
  PackagePlus, 
  Box, 
  Truck, 
  MapPin, 
  Calculator, 
  Headphones, 
  Globe, 
  DollarSign, 
  ShoppingBag, 
  LogOut, 
  LogIn, 
  Sparkles, 
  BookOpen, 
  Info, 
  ChevronDown, 
  Store, 
  Menu, 
  X,
  ChevronRight,
  UserCheck,
  RefreshCw,
  Ruler,
  Gavel,
  CreditCard,
  AlertTriangle,
  FileText,
  Smartphone,
  Crown
} from 'lucide-react';

const INFO_SUBMENU_ITEMS: { id: InfoTopicId; title: string; icon: React.ElementType; badge?: string }[] = [
  { id: 'servicos-precos', title: 'Serviços e Preços', icon: DollarSign, badge: 'Tabela 2026' },
  { id: 'calculadora-frete', title: 'Calculadora de Frete', icon: Calculator, badge: 'Simulador' },
  { id: 'blog-guias', title: 'Blog & Guias de Importação', icon: BookOpen, badge: 'Artigos' },
  { id: 'conversor-moedas', title: 'Conversor de Moedas', icon: RefreshCw, badge: 'JPY/BRL' },
  { id: 'tabela-tamanhos', title: 'Tabela de Tamanhos', icon: Ruler, badge: 'Roupas' },
  { id: 'como-comprar-mercari', title: 'Como Comprar no Mercari', icon: ShoppingBag, badge: 'Tutorial' },
  { id: 'como-dar-lances-yahoo', title: 'Leilões Yahoo! Japan', icon: Gavel, badge: 'Lances' },
  { id: 'formas-pagamento', title: 'Formas de Pagamento', icon: CreditCard, badge: 'PIX/Cartão' },
  { id: 'produtos-proibidos', title: 'Produtos Proibidos', icon: AlertTriangle, badge: 'Atenção' },
  { id: 'sites-compras-japao', title: 'Sites de Compras no Japão', icon: Globe },
  { id: 'impostos-importacao', title: 'Impostos de Importação', icon: FileText, badge: 'Alfandegário' },
  { id: 'salvar-app-celular', title: 'Instalar App no Celular', icon: Smartphone, badge: 'App 📱' },
];

interface NavbarProps {
  currentUser: UserProfile;
  isLoggedIn?: boolean;
  onLogout?: () => void;
  activeTab: string;
  setActiveTab: (tab: string) => void;
  onOpenAuth: () => void;
  onOpenNewTracking: () => void;
  onOpenAdminEntry: () => void;
  onOpenLogoModal?: () => void;
  onOpenClientImportModal?: () => void;
  onOpenAddStoreProductModal?: () => void;
  onOpenUserProfileModal?: () => void;
  onOpenGoogleDriveModal?: () => void;
  onOpenWhatsAppModal?: () => void;
  onOpenPaymentMethodsModal?: () => void;
  onOpenHomePopupModal?: () => void;
  onToggleAdminSidebar?: () => void;
  demoUsers: UserProfile[];
  onSwitchUser: (user: UserProfile) => void;
  receivedCount: number;
  pendingShipmentsCount?: number;
  notifications?: AppNotification[];
  onMarkNotificationAsRead?: (id: string) => void;
  onMarkAllNotificationsAsRead?: () => void;
}

export const Navbar: React.FC<NavbarProps> = ({
  currentUser,
  isLoggedIn = false,
  onLogout,
  activeTab,
  setActiveTab,
  onOpenAuth,
  onOpenNewTracking,
  onOpenAdminEntry,
  onOpenLogoModal,
  onOpenClientImportModal,
  onOpenAddStoreProductModal,
  onOpenUserProfileModal,
  onOpenGoogleDriveModal,
  onOpenWhatsAppModal,
  onOpenPaymentMethodsModal,
  onOpenHomePopupModal,
  onToggleAdminSidebar,
  demoUsers,
  onSwitchUser,
  receivedCount,
  pendingShipmentsCount = 0,
  notifications = [],
  onMarkNotificationAsRead = () => {},
  onMarkAllNotificationsAsRead = () => {}
}) => {
  const isAdmin = isLoggedIn && currentUser.role === 'admin';
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [infoModalTopic, setInfoModalTopic] = useState<InfoTopicId | null>(null);
  const [isInfoDropdownOpen, setIsInfoDropdownOpen] = useState(false);
  const [isMobileInfoSubmenuOpen, setIsMobileInfoSubmenuOpen] = useState(false);
  
  const navRef = useRef<HTMLDivElement>(null);
  const mobileMenuBtnRef = useRef<HTMLButtonElement>(null);
  const closeBtnRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);
  const desktopInfoDropdownRef = useRef<HTMLDivElement>(null);

  // Close mobile menu handler
  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    mobileMenuBtnRef.current?.focus();
  };

  const handleOpenTopic = (topicId: InfoTopicId | null) => {
    setIsInfoDropdownOpen(false);
    setIsMobileInfoSubmenuOpen(false);
    closeMobileMenu();

    const targetTopic = topicId || 'salvar-app-celular';
    window.dispatchEvent(new CustomEvent('openInfoTopic', { detail: targetTopic }));
    setActiveTab('info');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Click outside for Desktop Info Dropdown
  useEffect(() => {
    const handleClickOutsideDesktop = (e: MouseEvent) => {
      if (desktopInfoDropdownRef.current && !desktopInfoDropdownRef.current.contains(e.target as Node)) {
        setIsInfoDropdownOpen(false);
      }
    };
    const handleKeyDownDesktop = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsInfoDropdownOpen(false);
      }
    };

    if (isInfoDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutsideDesktop);
      window.addEventListener('keydown', handleKeyDownDesktop);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutsideDesktop);
      window.removeEventListener('keydown', handleKeyDownDesktop);
    };
  }, [isInfoDropdownOpen]);

  // Keyboard navigation, focus trapping & click outside detection
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isMobileMenuOpen) return;

      if (e.key === 'Escape') {
        closeMobileMenu();
        return;
      }

      if (e.key === 'Tab' && drawerRef.current) {
        const focusableElements = drawerRef.current.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
        );
        if (focusableElements.length === 0) return;

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      }
    };

    const handleClickOutside = (e: MouseEvent) => {
      if (isMobileMenuOpen && drawerRef.current && !drawerRef.current.contains(e.target as Node)) {
        if (mobileMenuBtnRef.current && mobileMenuBtnRef.current.contains(e.target as Node)) return;
        closeMobileMenu();
      }
    };

    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleKeyDown);
      document.addEventListener('mousedown', handleClickOutside);
      setTimeout(() => {
        closeBtnRef.current?.focus();
      }, 50);
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobileMenuOpen]);

  // Info topics now open directly in the page body via activeTab='info' in App.tsx

  const handleNavClick = (tabKey: string, sectionId?: string) => {
    setActiveTab(tabKey);
    closeMobileMenu();

    if (sectionId) {
      setTimeout(() => {
        const elem = document.getElementById(sectionId);
        if (elem) {
          elem.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  };

  return (
    <header className="bg-white/95 backdrop-blur-md border-b border-slate-200/80 sticky top-0 z-40 shadow-xs transition-all">
      {/* Top Notification / Role Switcher Strip */}
      <div className="bg-[#1D3557] text-white text-xs px-4 py-1.5 flex flex-wrap justify-between items-center gap-2">
        <div className="flex items-center gap-2.5">
          <span className="flex h-2 w-2 relative">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
          </span>
          <span className="font-medium text-slate-200 text-[11px] sm:text-xs">
            {isLoggedIn
              ? isAdmin 
                ? 'Modo Administrador do Depósito (Saitama, Japão)' 
                : `Sua Suíte Ativa no Japão: ${currentUser.suiteNumber}`
              : '👋 Você está navegando como visitante (Deslogado)'}
          </span>
        </div>

        {/* User Switcher Dropdown & Notification Center Bell */}
        <div className="flex items-center gap-2 sm:gap-3">
          {isAdmin && isLoggedIn && (
            <div className="hidden xl:flex items-center gap-1.5 px-2.5 py-0.5 bg-amber-500/20 text-amber-200 border border-amber-400/30 rounded-md text-[11px] font-extrabold">
              <Shield className="w-3 h-3 text-amber-400" />
              <span>Painel Admin Ativo</span>
            </div>
          )}

          {isLoggedIn && (
            <NotificationCenter
              notifications={notifications}
              onMarkAsRead={onMarkNotificationAsRead}
              onMarkAllAsRead={onMarkAllNotificationsAsRead}
            />
          )}

          <div className="flex items-center gap-2 pl-2 border-l border-slate-700">
            {isLoggedIn ? (
              <>
                {onOpenUserProfileModal && (
                  <button
                    onClick={onOpenUserProfileModal}
                    className="px-2.5 py-1 bg-slate-800 hover:bg-slate-700 text-sky-300 hover:text-white border border-slate-700 rounded-lg text-[11px] font-bold flex items-center gap-1 transition-colors cursor-pointer"
                    title="Editar Meus Dados Cadastrais e Endereço"
                  >
                    <User className="w-3 h-3 text-sky-400" />
                    <span className="hidden sm:inline">Meus Dados</span>
                  </button>
                )}

                {isAdmin ? (
                  <div className="flex items-center gap-1.5">
                    <span className="text-amber-400 font-extrabold hidden sm:inline text-[11px]">ADMIN:</span>
                    <select
                      aria-label="Selecionar usuário para simulação"
                      value={currentUser.uid}
                      onChange={(e) => {
                        const selected = demoUsers.find((u) => u.uid === e.target.value);
                        if (selected) onSwitchUser(selected);
                      }}
                      className="bg-amber-950/80 hover:bg-amber-900 text-amber-200 font-bold text-[11px] px-2 py-1 rounded-lg border border-amber-700 focus:outline-none cursor-pointer"
                    >
                      {demoUsers.map((u) => (
                        <option key={u.uid} value={u.uid}>
                          {u.role === 'admin' ? `👑 ${u.name} (Admin)` : `👤 ${u.name} (${u.suiteNumber})`}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="bg-slate-800 text-slate-100 font-bold text-[11px] px-2 py-1 rounded-lg border border-slate-700 flex items-center gap-1">
                      <UserCheck className="w-3 h-3 text-emerald-400" />
                      <span className="truncate max-w-[120px] sm:max-w-none">{currentUser.name}</span>
                    </span>
                  </div>
                )}

                {onLogout && (
                  <button
                    onClick={onLogout}
                    className="px-2 py-1 bg-red-950/80 hover:bg-red-900 text-red-300 border border-red-800 rounded-lg text-[11px] font-bold flex items-center gap-1 transition-colors cursor-pointer"
                    title="Sair da Conta"
                  >
                    <LogOut className="w-3 h-3 text-red-400" />
                    <span className="hidden sm:inline">Sair</span>
                  </button>
                )}
              </>
            ) : (
              <div className="flex items-center gap-1.5">
                <button
                  onClick={onOpenAuth}
                  className="px-2.5 py-1 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-[11px] font-extrabold flex items-center gap-1 transition-all cursor-pointer shadow-xs"
                >
                  <Sparkles className="w-3 h-3 text-amber-300" />
                  <span>Criar Conta</span>
                </button>

                <button
                  onClick={onOpenAuth}
                  className="px-2.5 py-1 bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 rounded-lg text-[11px] font-bold flex items-center gap-1 transition-colors cursor-pointer"
                >
                  <LogIn className="w-3 h-3 text-sky-400" />
                  <span>Entrar</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Nav Container */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" ref={navRef}>
        <div className="flex items-center justify-between h-16 sm:h-20">
          
          {/* Brand Logo */}
          <div className="flex items-center gap-3">
            <button 
              onClick={() => handleNavClick(isAdmin ? 'admin-packages' : 'dashboard')}
              className="focus:outline-none cursor-pointer focus:ring-2 focus:ring-purple-500 rounded-xl p-1 transition-all"
              aria-label="Página Inicial Do Japão À Sua Casa"
            >
              <Logo size="md" />
            </button>
          </div>

          {/* Desktop Navigation Links */}
          <nav 
            className="hidden xl:flex items-center gap-1.5" 
            role="navigation" 
            aria-label="Navegação Principal"
          >
            {!isAdmin ? (
              isLoggedIn ? (
                <>
                  {(!currentUser.subscriptionPlan || currentUser.subscriptionPlan === 'free') && (
                    <button
                      onClick={() => handleNavClick('subscription-plans')}
                      className="px-3 py-2 rounded-xl text-xs font-black transition-all flex items-center gap-1.5 whitespace-nowrap cursor-pointer bg-gradient-to-r from-red-600 to-orange-500 text-white shadow-xs hover:brightness-110 animate-pulse"
                      id="nav-btn-upgrade-plan-desktop"
                      title="Clique para assinar Plano VIP PRO ou BUSINESS"
                    >
                      <Crown className="w-4 h-4 text-amber-300 fill-amber-300" />
                      <span>Upgrade de Plano</span>
                    </button>
                  )}

                  <button
                    onClick={() => handleNavClick('dashboard')}
                    className={`px-3 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 whitespace-nowrap cursor-pointer ${
                      activeTab === 'dashboard'
                        ? 'bg-[#1D3557] text-white shadow-xs'
                        : 'text-slate-600 hover:text-[#1D3557] hover:bg-slate-100'
                    }`}
                  >
                    <MapPin className="w-4 h-4 text-sky-500" />
                    <span>Minha Suíte</span>
                  </button>

                  <button
                    onClick={() => handleNavClick('personal-shopper')}
                    className={`px-3 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 whitespace-nowrap cursor-pointer ${
                      activeTab === 'personal-shopper'
                        ? 'bg-[#1D3557] text-white shadow-xs'
                        : 'text-indigo-700 bg-indigo-50/80 hover:bg-indigo-100 border border-indigo-200/80'
                    }`}
                  >
                    <ShoppingBag className="w-4 h-4 text-indigo-600" />
                    <span>Personal Shopper</span>
                  </button>

                  <button
                    onClick={() => handleNavClick('nossa-loja')}
                    className={`px-3 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 whitespace-nowrap cursor-pointer ${
                      activeTab === 'nossa-loja'
                        ? 'bg-[#1D3557] text-white shadow-xs'
                        : 'text-red-700 bg-red-50/80 hover:bg-red-100 border border-red-200/80'
                    }`}
                  >
                    <Store className="w-4 h-4 text-[#E63946]" />
                    <span>Nossa Loja</span>
                  </button>

                  <button
                    onClick={() => handleNavClick('packages')}
                    className={`px-3 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 relative whitespace-nowrap cursor-pointer ${
                      activeTab === 'packages'
                        ? 'bg-[#1D3557] text-white shadow-xs'
                        : 'text-slate-600 hover:text-[#1D3557] hover:bg-slate-100'
                    }`}
                  >
                    <Box className="w-4 h-4" />
                    <span>Pacotes</span>
                    {receivedCount > 0 && (
                      <span className="px-1.5 py-0.5 bg-[#E63946] text-white font-black text-[10px] rounded-full">
                        {receivedCount}
                      </span>
                    )}
                  </button>

                  <button
                    onClick={() => handleNavClick('shipments')}
                    className={`px-3 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 whitespace-nowrap cursor-pointer ${
                      activeTab === 'shipments'
                        ? 'bg-[#1D3557] text-white shadow-xs'
                        : 'text-slate-600 hover:text-[#1D3557] hover:bg-slate-100'
                    }`}
                  >
                    <Truck className="w-4 h-4" />
                    <span>Consolidações</span>
                  </button>

                  <button
                    onClick={() => handleNavClick('support')}
                    className={`px-3 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 whitespace-nowrap cursor-pointer ${
                      activeTab === 'support'
                        ? 'bg-[#1D3557] text-white shadow-xs'
                        : 'text-slate-600 hover:text-[#1D3557] hover:bg-slate-100'
                    }`}
                  >
                    <Headphones className="w-4 h-4 text-emerald-500" />
                    <span>Suporte</span>
                  </button>

                  <div className="relative" ref={desktopInfoDropdownRef}>
                    <button
                      type="button"
                      onClick={() => setIsInfoDropdownOpen(prev => !prev)}
                      aria-expanded={isInfoDropdownOpen}
                      aria-haspopup="true"
                      aria-controls="desktop-info-submenu"
                      className="px-3 py-2 rounded-xl text-xs font-bold text-red-700 bg-red-50/80 hover:bg-red-100 border border-red-200/80 transition-all flex items-center gap-1.5 whitespace-nowrap cursor-pointer shadow-2xs"
                    >
                      <Info className="w-4 h-4 text-red-600" />
                      <span>Informações</span>
                      <ChevronDown className={`w-3.5 h-3.5 text-red-500 transition-transform duration-200 ${isInfoDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {isInfoDropdownOpen && (
                      <div
                        id="desktop-info-submenu"
                        role="menu"
                        aria-label="Submenu da Central de Informações"
                        className="absolute right-0 top-full mt-2 w-72 bg-white rounded-2xl shadow-xl border border-slate-200 py-2 z-50 animate-fade-in text-left overflow-hidden"
                      >
                        <div className="px-3.5 py-1.5 border-b border-slate-100 text-[10px] font-black uppercase text-slate-400 tracking-wider flex items-center justify-between">
                          <span>Central de Informações</span>
                          <span className="text-[9px] bg-red-50 text-red-600 px-1.5 py-0.5 rounded font-mono">Guias & Ajuda</span>
                        </div>
                        <div className="max-h-80 overflow-y-auto py-1">
                          {INFO_SUBMENU_ITEMS.map((item) => {
                            const IconComp = item.icon;
                            return (
                              <button
                                key={item.id}
                                role="menuitem"
                                onClick={() => handleOpenTopic(item.id)}
                                className="w-full text-left px-3.5 py-2 hover:bg-red-50/60 flex items-center justify-between text-xs font-semibold text-slate-700 hover:text-red-700 transition-colors cursor-pointer"
                              >
                                <div className="flex items-center gap-2.5 min-w-0">
                                  <IconComp className="w-4 h-4 text-red-600 shrink-0" />
                                  <span className="truncate">{item.title}</span>
                                </div>
                                {item.badge && (
                                  <span className="text-[10px] font-extrabold px-1.5 py-0.5 bg-red-50 text-red-600 rounded-md border border-red-100 shrink-0 ml-1">
                                    {item.badge}
                                  </span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                        <div className="p-1.5 border-t border-slate-100 bg-slate-50/70">
                          <button
                            role="menuitem"
                            onClick={() => handleOpenTopic(null)}
                            className="w-full text-center py-2 px-3 text-xs font-bold text-red-700 hover:text-red-800 bg-red-100/80 hover:bg-red-200/80 rounded-xl transition-all cursor-pointer flex items-center justify-center gap-1.5"
                          >
                            <Sparkles className="w-3.5 h-3.5 text-amber-500" />
                            <span>Abrir Central Completa</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <button
                    onClick={() => handleNavClick('landing', 'como-funciona')}
                    className={`px-3 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 whitespace-nowrap cursor-pointer ${
                      activeTab === 'landing'
                        ? 'bg-[#1D3557] text-white shadow-xs'
                        : 'text-sky-700 bg-sky-50/80 hover:bg-sky-100 border border-sky-200/80'
                    }`}
                  >
                    <Globe className="w-4 h-4 text-sky-600" />
                    <span>Como Funciona</span>
                  </button>

                  <button
                    onClick={() => handleNavClick('landing', 'servicos-e-precos')}
                    className="px-3 py-2 rounded-xl text-xs font-bold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 transition-all flex items-center gap-1.5 border border-emerald-200/80 cursor-pointer whitespace-nowrap"
                  >
                    <DollarSign className="w-4 h-4 text-emerald-600" />
                    <span>Serviços & Preços</span>
                  </button>

                  <button
                    onClick={() => handleNavClick('subscription-plans')}
                    className={`px-3 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 whitespace-nowrap cursor-pointer ${
                      activeTab === 'subscription-plans'
                        ? 'bg-gradient-to-r from-red-600 to-orange-500 text-white shadow-xs'
                        : 'text-red-700 bg-red-50 hover:bg-red-100 border border-red-200/80'
                    }`}
                  >
                    <Crown className="w-4 h-4 text-amber-500 fill-amber-500/30" />
                    <span>Planos VIP</span>
                  </button>

                  <button
                    onClick={() => handleNavClick('personal-shopper')}
                    className={`px-3 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 whitespace-nowrap cursor-pointer ${
                      activeTab === 'personal-shopper'
                        ? 'bg-[#1D3557] text-white shadow-xs'
                        : 'text-indigo-700 bg-indigo-50/80 hover:bg-indigo-100 border border-indigo-200/80'
                    }`}
                  >
                    <ShoppingBag className="w-4 h-4 text-indigo-600" />
                    <span>Personal Shopper</span>
                  </button>

                  <button
                    onClick={() => handleNavClick('nossa-loja')}
                    className={`px-3 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 whitespace-nowrap cursor-pointer ${
                      activeTab === 'nossa-loja'
                        ? 'bg-[#1D3557] text-white shadow-xs'
                        : 'text-red-700 bg-red-50/80 hover:bg-red-100 border border-red-200/80'
                    }`}
                  >
                    <Store className="w-4 h-4 text-[#E63946]" />
                    <span>Nossa Loja</span>
                  </button>

                  <button
                    onClick={() => handleNavClick('calculator')}
                    className={`px-3 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 whitespace-nowrap cursor-pointer ${
                      activeTab === 'calculator'
                        ? 'bg-[#1D3557] text-white shadow-xs'
                        : 'text-slate-600 hover:text-[#1D3557] hover:bg-slate-100'
                    }`}
                  >
                    <Calculator className="w-4 h-4 text-amber-500" />
                    <span>Calculadora Frete</span>
                  </button>

                  <button
                    onClick={() => handleNavClick('support')}
                    className={`px-3 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 whitespace-nowrap cursor-pointer ${
                      activeTab === 'support'
                        ? 'bg-[#1D3557] text-white shadow-xs'
                        : 'text-slate-600 hover:text-[#1D3557] hover:bg-slate-100'
                    }`}
                  >
                    <Headphones className="w-4 h-4 text-emerald-500" />
                    <span>Suporte</span>
                  </button>

                  <div className="relative" ref={desktopInfoDropdownRef}>
                    <button
                      type="button"
                      onClick={() => setIsInfoDropdownOpen(prev => !prev)}
                      aria-expanded={isInfoDropdownOpen}
                      aria-haspopup="true"
                      aria-controls="desktop-info-submenu"
                      className="px-3 py-2 rounded-xl text-xs font-bold text-red-700 bg-red-50/80 hover:bg-red-100 border border-red-200/80 transition-all flex items-center gap-1.5 whitespace-nowrap cursor-pointer shadow-2xs"
                    >
                      <Info className="w-4 h-4 text-red-600" />
                      <span>Informações</span>
                      <ChevronDown className={`w-3.5 h-3.5 text-red-500 transition-transform duration-200 ${isInfoDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {isInfoDropdownOpen && (
                      <div
                        id="desktop-info-submenu"
                        role="menu"
                        aria-label="Submenu da Central de Informações"
                        className="absolute right-0 top-full mt-2 w-72 bg-white rounded-2xl shadow-xl border border-slate-200 py-2 z-50 animate-fade-in text-left overflow-hidden"
                      >
                        <div className="px-3.5 py-1.5 border-b border-slate-100 text-[10px] font-black uppercase text-slate-400 tracking-wider flex items-center justify-between">
                          <span>Central de Informações</span>
                          <span className="text-[9px] bg-red-50 text-red-600 px-1.5 py-0.5 rounded font-mono">Guias & Ajuda</span>
                        </div>
                        <div className="max-h-80 overflow-y-auto py-1">
                          {INFO_SUBMENU_ITEMS.map((item) => {
                            const IconComp = item.icon;
                            return (
                              <button
                                key={item.id}
                                role="menuitem"
                                onClick={() => handleOpenTopic(item.id)}
                                className="w-full text-left px-3.5 py-2 hover:bg-red-50/60 flex items-center justify-between text-xs font-semibold text-slate-700 hover:text-red-700 transition-colors cursor-pointer"
                              >
                                <div className="flex items-center gap-2.5 min-w-0">
                                  <IconComp className="w-4 h-4 text-red-600 shrink-0" />
                                  <span className="truncate">{item.title}</span>
                                </div>
                                {item.badge && (
                                  <span className="text-[10px] font-extrabold px-1.5 py-0.5 bg-red-50 text-red-600 rounded-md border border-red-100 shrink-0 ml-1">
                                    {item.badge}
                                  </span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                        <div className="p-1.5 border-t border-slate-100 bg-slate-50/70">
                          <button
                            role="menuitem"
                            onClick={() => handleOpenTopic(null)}
                            className="w-full text-center py-2 px-3 text-xs font-bold text-red-700 hover:text-red-800 bg-red-100/80 hover:bg-red-200/80 rounded-xl transition-all cursor-pointer flex items-center justify-center gap-1.5"
                          >
                            <Sparkles className="w-3.5 h-3.5 text-amber-500" />
                            <span>Abrir Central Completa</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )
            ) : (
              /* ADMIN LINKS - "Como Funciona" is hidden when logged in as admin */
              <>
                <button
                  onClick={() => handleNavClick('admin-finances')}
                  className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer ${
                    activeTab === 'admin-finances'
                      ? 'bg-emerald-600 text-white shadow-xs'
                      : 'text-emerald-800 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200/80'
                  }`}
                  id="nav-btn-admin-finances"
                >
                  <DollarSign className="w-4 h-4 text-emerald-600" />
                  <span>Financeiro & Balanço 📊</span>
                </button>

                <button
                  onClick={() => handleNavClick('nossa-loja')}
                  className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer ${
                    activeTab === 'nossa-loja'
                      ? 'bg-[#1D3557] text-white shadow-xs'
                      : 'text-red-700 bg-red-50 hover:bg-red-100 border border-red-200/80'
                  }`}
                  id="nav-btn-admin-loja"
                  title="Atalho direto para a Nossa Loja"
                >
                  <Store className="w-4 h-4 text-[#E63946]" />
                  <span>Atalho pra Loja 🛍️</span>
                </button>

                <button
                  onClick={() => handleNavClick('admin-packages')}
                  className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer ${
                    activeTab === 'admin-packages'
                      ? 'bg-[#1D3557] text-white shadow-xs'
                      : 'text-slate-600 hover:text-[#1D3557] hover:bg-slate-100'
                  }`}
                >
                  <Box className="w-4 h-4" />
                  <span>Estoque Pacotes</span>
                </button>

                <button
                  onClick={() => handleNavClick('admin-shipments')}
                  className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer ${
                    activeTab === 'admin-shipments'
                      ? 'bg-[#1D3557] text-white shadow-xs'
                      : 'text-slate-600 hover:text-[#1D3557] hover:bg-slate-100'
                  }`}
                >
                  <Truck className="w-4 h-4 text-sky-400" />
                  <span>Pedidos Envio</span>
                  {pendingShipmentsCount > 0 ? (
                    <span className="px-1.5 py-0.5 text-[10px] font-black bg-rose-500 text-white rounded-full animate-pulse shadow-xs">
                      {pendingShipmentsCount} em espera
                    </span>
                  ) : (
                    <span className="text-[10px] text-slate-400 font-normal">(0)</span>
                  )}
                </button>

                <button
                  onClick={() => handleNavClick('admin-clients')}
                  className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer ${
                    activeTab === 'admin-clients'
                      ? 'bg-[#1D3557] text-white shadow-xs'
                      : 'text-amber-800 bg-amber-50 hover:bg-amber-100 border border-amber-200/80'
                  }`}
                >
                  <Shield className="w-4 h-4 text-amber-600" />
                  <span>Gestão de Suítes</span>
                </button>

                <button
                  onClick={() => handleNavClick('personal-shopper')}
                  className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer ${
                    activeTab === 'personal-shopper'
                      ? 'bg-[#1D3557] text-white shadow-xs'
                      : 'text-indigo-700 bg-indigo-50/80 hover:bg-indigo-100 border border-indigo-200/80'
                  }`}
                >
                  <ShoppingBag className="w-4 h-4 text-indigo-600" />
                  <span>Personal Shopper</span>
                </button>

                <button
                  onClick={() => handleNavClick('support')}
                  className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer ${
                    activeTab === 'support'
                      ? 'bg-[#1D3557] text-white shadow-xs'
                      : 'text-slate-600 hover:text-[#1D3557] hover:bg-slate-100'
                  }`}
                >
                  <Headphones className="w-4 h-4 text-emerald-500" />
                  <span>Suporte</span>
                </button>

                <button
                  onClick={() => handleNavClick('blog')}
                  className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer ${
                    activeTab === 'blog'
                      ? 'bg-[#1D3557] text-white shadow-xs'
                      : 'text-red-700 bg-red-50/80 hover:bg-red-100 border border-red-200/80'
                  }`}
                >
                  <BookOpen className="w-4 h-4 text-red-600" />
                  <span>Blog Admin</span>
                </button>
              </>
            )}
          </nav>

          {/* Right Actions & Mobile Toggle Button */}
          <div className="flex items-center gap-2 sm:gap-3">
            {!isAdmin ? (
              isLoggedIn && (
                <button
                  onClick={() => {
                    onOpenNewTracking();
                    closeMobileMenu();
                  }}
                  className="inline-flex items-center gap-2 px-3.5 py-2 bg-[#E63946] hover:bg-[#d62839] active:scale-95 text-white font-bold text-xs sm:text-sm rounded-xl shadow-md shadow-red-500/10 transition-all cursor-pointer"
                  id="btn-cadastrar-novo-rastreio"
                >
                  <PackagePlus className="w-4 h-4 text-white" />
                  <span className="hidden sm:inline">Cadastrar Rastreio</span>
                  <span className="sm:hidden">Rastreio</span>
                </button>
              )
            ) : (
              <button
                onClick={() => {
                  onOpenAdminEntry();
                  closeMobileMenu();
                }}
                className="inline-flex items-center gap-2 px-3.5 py-2 bg-[#1D3557] hover:bg-slate-800 active:scale-95 text-white font-bold text-xs sm:text-sm rounded-xl shadow-md transition-all cursor-pointer"
                id="btn-dar-entrada-pacote"
              >
                <Box className="w-4 h-4 text-amber-300" />
                <span>Entrada Pacote</span>
              </button>
            )}

            {/* Mobile Hamburger Toggle Button */}
            <button
              ref={mobileMenuBtnRef}
              type="button"
              onClick={() => setIsMobileMenuOpen(prev => !prev)}
              aria-label={isMobileMenuOpen ? "Fechar menu de navegação" : "Abrir menu de navegação"}
              aria-expanded={isMobileMenuOpen}
              aria-haspopup="dialog"
              aria-controls="mobile-navigation-menu"
              className="xl:hidden p-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 cursor-pointer"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6 text-[#E63946]" />
              ) : (
                <Menu className="w-6 h-6 text-[#1D3557]" />
              )}
            </button>
          </div>

        </div>
      </div>

      {/* Responsive & Accessible Mobile Drawer Overlay Menu */}
      {isMobileMenuOpen && createPortal(
        <div 
          className="fixed inset-0 z-[9999] xl:hidden bg-slate-950/70 backdrop-blur-xs transition-opacity animate-fade-in-overlay"
          onClick={closeMobileMenu}
          aria-hidden={!isMobileMenuOpen}
        >
          <div 
            id="mobile-navigation-menu"
            ref={drawerRef}
            tabIndex={-1}
            role="dialog"
            aria-modal="true"
            aria-label="Menu de Navegação Principal"
            className="fixed top-0 right-0 w-full max-w-xs sm:max-w-sm h-full bg-white shadow-2xl border-l border-slate-200 flex flex-col justify-between p-5 overflow-y-auto animate-slide-right-side z-[10000]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Drawer Header */}
            <div className="space-y-4">
              <div className="flex items-center justify-between border-b border-slate-100 pb-4">
                <Logo size="sm" />
                <button
                  ref={closeBtnRef}
                  onClick={closeMobileMenu}
                  aria-label="Fechar menu de navegação"
                  className="p-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 cursor-pointer"
                >
                  <X className="w-5 h-5 text-slate-700" />
                </button>
              </div>

              {/* User status card in drawer */}
              <div className="p-3 bg-slate-50 rounded-2xl border border-slate-200/80 space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-bold text-slate-700">
                    {isLoggedIn ? currentUser.name : 'Visitante'}
                  </span>
                  {isLoggedIn && (
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-black uppercase bg-purple-100 text-purple-700">
                      {isAdmin ? 'ADMINISTRADOR' : `SUÍTE ${currentUser.suiteNumber}`}
                    </span>
                  )}
                </div>
                {isLoggedIn && (
                  <p className="text-[11px] text-slate-500">Logado no sistema Do Japão À Sua Casa</p>
                )}
              </div>

              {/* Navigation Links in Mobile Drawer */}
              <div className="space-y-1.5 pt-2">
                <span className="text-[10px] font-black uppercase tracking-wider text-slate-400 px-2 block">
                  Navegação do Sistema
                </span>

                {!isAdmin ? (
                  isLoggedIn ? (
                    <>
                      {/* Upgrade Button for Free Plan Users in Mobile Drawer */}
                      {(!currentUser.subscriptionPlan || currentUser.subscriptionPlan === 'free') && (
                        <button
                          onClick={() => handleNavClick('subscription-plans')}
                          className="w-full py-3 px-3.5 rounded-xl text-xs font-black flex items-center justify-between transition-all cursor-pointer bg-gradient-to-r from-red-600 via-orange-500 to-amber-500 text-white shadow-md hover:brightness-110 animate-pulse my-1 border border-amber-300/40"
                          id="drawer-btn-upgrade-plan-free"
                        >
                          <span className="flex items-center gap-2">
                            <Crown className="w-4 h-4 text-amber-300 fill-amber-300" />
                            <span>Upgrade de Plano VIP</span>
                          </span>
                          <span className="px-2 py-0.5 text-[9px] bg-slate-950/40 text-amber-200 font-black rounded-md uppercase border border-amber-300/30">
                            PRO / BUSINESS
                          </span>
                        </button>
                      )}

                      <button
                        onClick={() => handleNavClick('dashboard')}
                        className={`w-full py-3 px-3.5 rounded-xl text-xs font-bold flex items-center justify-between transition-all cursor-pointer ${
                          activeTab === 'dashboard' ? 'bg-[#1D3557] text-white' : 'text-slate-700 hover:bg-slate-100'
                        }`}
                      >
                        <span className="flex items-center gap-2.5">
                          <MapPin className="w-4 h-4 text-sky-500" />
                          <span>Minha Suíte</span>
                        </span>
                        <ChevronRight className="w-4 h-4 opacity-50" />
                      </button>

                      <button
                        onClick={() => handleNavClick('personal-shopper')}
                        className={`w-full py-3 px-3.5 rounded-xl text-xs font-bold flex items-center justify-between transition-all cursor-pointer ${
                          activeTab === 'personal-shopper' ? 'bg-[#1D3557] text-white' : 'text-slate-700 hover:bg-indigo-50'
                        }`}
                      >
                        <span className="flex items-center gap-2.5">
                          <ShoppingBag className="w-4 h-4 text-indigo-600" />
                          <span>Personal Shopper</span>
                        </span>
                        <ChevronRight className="w-4 h-4 opacity-50" />
                      </button>

                      <button
                        onClick={() => handleNavClick('nossa-loja')}
                        className={`w-full py-3 px-3.5 rounded-xl text-xs font-bold flex items-center justify-between transition-all cursor-pointer ${
                          activeTab === 'nossa-loja' ? 'bg-[#1D3557] text-white' : 'text-slate-700 hover:bg-red-50'
                        }`}
                      >
                        <span className="flex items-center gap-2.5">
                          <Store className="w-4 h-4 text-[#E63946]" />
                          <span>Nossa Loja (Vendas & Rifas)</span>
                        </span>
                        <ChevronRight className="w-4 h-4 opacity-50" />
                      </button>

                      <button
                        onClick={() => handleNavClick('packages')}
                        className={`w-full py-3 px-3.5 rounded-xl text-xs font-bold flex items-center justify-between transition-all cursor-pointer ${
                          activeTab === 'packages' ? 'bg-[#1D3557] text-white' : 'text-slate-700 hover:bg-slate-100'
                        }`}
                      >
                        <span className="flex items-center gap-2.5">
                          <Box className="w-4 h-4 text-amber-500" />
                          <span>Pacotes Recebidos</span>
                        </span>
                        {receivedCount > 0 ? (
                          <span className="px-2 py-0.5 bg-[#E63946] text-white font-black text-[10px] rounded-full">
                            {receivedCount}
                          </span>
                        ) : (
                          <ChevronRight className="w-4 h-4 opacity-50" />
                        )}
                      </button>

                      <button
                        onClick={() => handleNavClick('shipments')}
                        className={`w-full py-3 px-3.5 rounded-xl text-xs font-bold flex items-center justify-between transition-all cursor-pointer ${
                          activeTab === 'shipments' ? 'bg-[#1D3557] text-white' : 'text-slate-700 hover:bg-slate-100'
                        }`}
                      >
                        <span className="flex items-center gap-2.5">
                          <Truck className="w-4 h-4 text-emerald-600" />
                          <span>Consolidações & Envio</span>
                        </span>
                        <ChevronRight className="w-4 h-4 opacity-50" />
                      </button>

                      <button
                        onClick={() => handleNavClick('support')}
                        className={`w-full py-3 px-3.5 rounded-xl text-xs font-bold flex items-center justify-between transition-all cursor-pointer ${
                          activeTab === 'support' ? 'bg-[#1D3557] text-white' : 'text-slate-700 hover:bg-slate-100'
                        }`}
                      >
                        <span className="flex items-center gap-2.5">
                          <Headphones className="w-4 h-4 text-emerald-500" />
                          <span>Suporte & WhatsApp</span>
                        </span>
                        <ChevronRight className="w-4 h-4 opacity-50" />
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => handleNavClick('landing', 'como-funciona')}
                        className="w-full py-3 px-3.5 rounded-xl text-xs font-bold text-slate-700 hover:bg-sky-50 flex items-center justify-between transition-all cursor-pointer"
                      >
                        <span className="flex items-center gap-2.5">
                          <Globe className="w-4 h-4 text-sky-600" />
                          <span>Como Funciona</span>
                        </span>
                        <ChevronRight className="w-4 h-4 opacity-50" />
                      </button>

                      <button
                        onClick={() => handleNavClick('landing', 'servicos-e-precos')}
                        className="w-full py-3 px-3.5 rounded-xl text-xs font-bold text-slate-700 hover:bg-emerald-50 flex items-center justify-between transition-all cursor-pointer"
                      >
                        <span className="flex items-center gap-2.5">
                          <DollarSign className="w-4 h-4 text-emerald-600" />
                          <span>Serviços & Tabela de Preços</span>
                        </span>
                        <ChevronRight className="w-4 h-4 opacity-50" />
                      </button>

                      <button
                        onClick={() => handleNavClick('subscription-plans')}
                        className="w-full py-3 px-3.5 rounded-xl text-xs font-bold text-slate-700 hover:bg-red-50 flex items-center justify-between transition-all cursor-pointer"
                      >
                        <span className="flex items-center gap-2.5">
                          <Crown className="w-4 h-4 text-amber-500" />
                          <span>Planos de Assinatura VIP</span>
                        </span>
                        <span className="px-2 py-0.5 text-[10px] bg-red-100 text-red-700 font-bold rounded-md border border-red-200">
                          Pro / Business
                        </span>
                      </button>

                      <button
                        onClick={() => handleNavClick('personal-shopper')}
                        className="w-full py-3 px-3.5 rounded-xl text-xs font-bold text-slate-700 hover:bg-indigo-50 flex items-center justify-between transition-all cursor-pointer"
                      >
                        <span className="flex items-center gap-2.5">
                          <ShoppingBag className="w-4 h-4 text-indigo-600" />
                          <span>Personal Shopper</span>
                        </span>
                        <ChevronRight className="w-4 h-4 opacity-50" />
                      </button>

                      <button
                        onClick={() => handleNavClick('nossa-loja')}
                        className="w-full py-3 px-3.5 rounded-xl text-xs font-bold text-slate-700 hover:bg-red-50 flex items-center justify-between transition-all cursor-pointer"
                      >
                        <span className="flex items-center gap-2.5">
                          <Store className="w-4 h-4 text-[#E63946]" />
                          <span>Nossa Loja Oficial</span>
                        </span>
                        <ChevronRight className="w-4 h-4 opacity-50" />
                      </button>

                      <button
                        onClick={() => handleNavClick('calculator')}
                        className="w-full py-3 px-3.5 rounded-xl text-xs font-bold text-slate-700 hover:bg-slate-100 flex items-center justify-between transition-all cursor-pointer"
                      >
                        <span className="flex items-center gap-2.5">
                          <Calculator className="w-4 h-4 text-amber-500" />
                          <span>Calculadora de Frete</span>
                        </span>
                        <ChevronRight className="w-4 h-4 opacity-50" />
                      </button>

                      <button
                        onClick={() => handleNavClick('support')}
                        className="w-full py-3 px-3.5 rounded-xl text-xs font-bold text-slate-700 hover:bg-slate-100 flex items-center justify-between transition-all cursor-pointer"
                      >
                        <span className="flex items-center gap-2.5">
                          <Headphones className="w-4 h-4 text-emerald-500" />
                          <span>Suporte & Dúvidas</span>
                        </span>
                        <ChevronRight className="w-4 h-4 opacity-50" />
                      </button>
                    </>
                  )
                ) : (
                  /* Admin Drawer Links ("Como Funciona" is strictly excluded) */
                  <>
                    <button
                      onClick={() => handleNavClick('admin-finances')}
                      className={`w-full py-3 px-3.5 rounded-xl text-xs font-bold flex items-center justify-between transition-all cursor-pointer ${
                        activeTab === 'admin-finances' ? 'bg-emerald-600 text-white' : 'bg-emerald-50 text-emerald-900 hover:bg-emerald-100 border border-emerald-200'
                      }`}
                    >
                      <span className="flex items-center gap-2.5">
                        <DollarSign className="w-4 h-4 text-emerald-600" />
                        <span>Financeiro & Balanço 📊</span>
                      </span>
                      <ChevronRight className="w-4 h-4 opacity-50" />
                    </button>

                    <button
                      onClick={() => handleNavClick('nossa-loja')}
                      className={`w-full py-3 px-3.5 rounded-xl text-xs font-bold flex items-center justify-between transition-all cursor-pointer ${
                        activeTab === 'nossa-loja' ? 'bg-[#1D3557] text-white' : 'bg-red-50 text-red-900 hover:bg-red-100 border border-red-200'
                      }`}
                    >
                      <span className="flex items-center gap-2.5">
                        <Store className="w-4 h-4 text-[#E63946]" />
                        <span>Atalho pra Loja (Nossa Loja)</span>
                      </span>
                      <ChevronRight className="w-4 h-4 opacity-50" />
                    </button>

                    <button
                      onClick={() => handleNavClick('admin-packages')}
                      className={`w-full py-3 px-3.5 rounded-xl text-xs font-bold flex items-center justify-between transition-all cursor-pointer ${
                        activeTab === 'admin-packages' ? 'bg-[#1D3557] text-white' : 'text-slate-700 hover:bg-slate-100'
                      }`}
                    >
                      <span className="flex items-center gap-2.5">
                        <Box className="w-4 h-4 text-amber-400" />
                        <span>Estoque de Pacotes (Admin)</span>
                      </span>
                      <ChevronRight className="w-4 h-4 opacity-50" />
                    </button>

                    <button
                      onClick={() => handleNavClick('admin-shipments')}
                      className={`w-full py-3 px-3.5 rounded-xl text-xs font-bold flex items-center justify-between transition-all cursor-pointer ${
                        activeTab === 'admin-shipments' ? 'bg-[#1D3557] text-white' : 'text-slate-700 hover:bg-slate-100'
                      }`}
                    >
                      <span className="flex items-center gap-2.5">
                        <Truck className="w-4 h-4 text-sky-400" />
                        <span>
                          {pendingShipmentsCount > 0
                            ? `Pedidos de Envio (${pendingShipmentsCount} em espera)`
                            : 'Pedidos de Envio (Admin)'}
                        </span>
                      </span>
                      {pendingShipmentsCount > 0 ? (
                        <span className="px-2 py-0.5 bg-rose-500 text-white font-extrabold text-[11px] rounded-full animate-pulse shadow-sm">
                          {pendingShipmentsCount}
                        </span>
                      ) : (
                        <ChevronRight className="w-4 h-4 opacity-50" />
                      )}
                    </button>

                    <button
                      onClick={() => handleNavClick('admin-clients')}
                      className={`w-full py-3 px-3.5 rounded-xl text-xs font-bold flex items-center justify-between transition-all cursor-pointer ${
                        activeTab === 'admin-clients' ? 'bg-[#1D3557] text-white' : 'text-slate-700 hover:bg-slate-100'
                      }`}
                    >
                      <span className="flex items-center gap-2.5">
                        <Shield className="w-4 h-4 text-amber-600" />
                        <span>Gestão de Suítes</span>
                      </span>
                      <ChevronRight className="w-4 h-4 opacity-50" />
                    </button>

                    <button
                      onClick={() => handleNavClick('personal-shopper')}
                      className={`w-full py-3 px-3.5 rounded-xl text-xs font-bold flex items-center justify-between transition-all cursor-pointer ${
                        activeTab === 'personal-shopper' ? 'bg-[#1D3557] text-white' : 'text-slate-700 hover:bg-slate-100'
                      }`}
                    >
                      <span className="flex items-center gap-2.5">
                        <ShoppingBag className="w-4 h-4 text-indigo-600" />
                        <span>Personal Shopper</span>
                      </span>
                      <ChevronRight className="w-4 h-4 opacity-50" />
                    </button>

                    <button
                      onClick={() => handleNavClick('support')}
                      className={`w-full py-3 px-3.5 rounded-xl text-xs font-bold flex items-center justify-between transition-all cursor-pointer ${
                        activeTab === 'support' ? 'bg-[#1D3557] text-white' : 'text-slate-700 hover:bg-slate-100'
                      }`}
                    >
                      <span className="flex items-center gap-2.5">
                        <Headphones className="w-4 h-4 text-emerald-500" />
                        <span>Atendimento Suporte</span>
                      </span>
                      <ChevronRight className="w-4 h-4 opacity-50" />
                    </button>

                    <button
                      onClick={() => handleNavClick('blog')}
                      className={`w-full py-3 px-3.5 rounded-xl text-xs font-bold flex items-center justify-between transition-all cursor-pointer ${
                        activeTab === 'blog' ? 'bg-[#1D3557] text-white' : 'text-slate-700 hover:bg-slate-100'
                      }`}
                    >
                      <span className="flex items-center gap-2.5">
                        <BookOpen className="w-4 h-4 text-red-600" />
                        <span>Blog Admin</span>
                      </span>
                      <ChevronRight className="w-4 h-4 opacity-50" />
                    </button>
                  </>
                )}

                <div className="space-y-1 pt-1">
                  <button
                    type="button"
                    onClick={() => setIsMobileInfoSubmenuOpen(prev => !prev)}
                    aria-expanded={isMobileInfoSubmenuOpen}
                    aria-controls="mobile-info-submenu"
                    className="w-full py-3 px-3.5 rounded-xl text-xs font-bold text-red-700 bg-red-50 hover:bg-red-100 flex items-center justify-between transition-all cursor-pointer"
                  >
                    <span className="flex items-center gap-2.5">
                      <Info className="w-4 h-4 text-red-600" />
                      <span>Central de Informações</span>
                    </span>
                    <ChevronDown className={`w-4 h-4 text-red-500 transition-transform duration-200 ${isMobileInfoSubmenuOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {isMobileInfoSubmenuOpen && (
                    <div
                      id="mobile-info-submenu"
                      role="region"
                      aria-label="Submenu da Central de Informações"
                      className="space-y-1 pl-3 py-1.5 border-l-2 border-red-300 ml-3 animate-fade-in"
                    >
                      {INFO_SUBMENU_ITEMS.map((item) => {
                        const IconComp = item.icon;
                        return (
                          <button
                            key={item.id}
                            onClick={() => handleOpenTopic(item.id)}
                            className="w-full text-left py-2 px-2.5 rounded-lg hover:bg-red-50 flex items-center justify-between text-xs font-medium text-slate-700 transition-colors cursor-pointer"
                          >
                            <span className="flex items-center gap-2 min-w-0 pr-1">
                              <IconComp className="w-3.5 h-3.5 text-red-600 shrink-0" />
                              <span className="truncate">{item.title}</span>
                            </span>
                            {item.badge && (
                              <span className="text-[9px] font-extrabold px-1.5 py-0.5 bg-red-50 text-red-600 rounded-md border border-red-100 shrink-0">
                                {item.badge}
                              </span>
                            )}
                          </button>
                        );
                      })}

                      <button
                        onClick={() => handleOpenTopic(null)}
                        className="w-full py-2 px-3 text-xs font-bold text-red-700 bg-red-100 hover:bg-red-200 rounded-xl transition-all cursor-pointer flex items-center justify-center gap-1.5 mt-2"
                      >
                        <Sparkles className="w-3.5 h-3.5 text-amber-500" />
                        <span>Abrir Central Completa</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Drawer Footer */}
            <div className="pt-4 border-t border-slate-100 space-y-3">
              {isLoggedIn ? (
                <div className="space-y-2">
                  {onOpenUserProfileModal && (
                    <button
                      onClick={() => {
                        onOpenUserProfileModal();
                        closeMobileMenu();
                      }}
                      className="w-full py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-800 font-bold rounded-xl text-xs flex items-center justify-center gap-2 transition-colors cursor-pointer"
                    >
                      <User className="w-4 h-4 text-sky-600" />
                      <span>Meus Dados Cadastrais</span>
                    </button>
                  )}

                  {onLogout && (
                    <button
                      onClick={() => {
                        onLogout();
                        closeMobileMenu();
                      }}
                      className="w-full py-2.5 bg-red-50 hover:bg-red-100 text-red-700 font-bold rounded-xl text-xs flex items-center justify-center gap-2 transition-colors cursor-pointer"
                    >
                      <LogOut className="w-4 h-4 text-red-600" />
                      <span>Sair da Conta</span>
                    </button>
                  )}
                </div>
              ) : (
                <button
                  onClick={() => {
                    onOpenAuth();
                    closeMobileMenu();
                  }}
                  className="w-full py-3 bg-[#1D3557] hover:bg-slate-800 text-white font-black rounded-xl text-xs flex items-center justify-center gap-2 shadow-md cursor-pointer"
                >
                  <LogIn className="w-4 h-4 text-sky-400" />
                  <span>Entrar / Cadastrar Conta</span>
                </button>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </header>
  );
};

export default Navbar;
